import React, { useCallback, useState } from 'react';
import { useIntl } from 'react-intl';

import { useTheme } from '@mui/styles';
import Grid from '@mui/material/Grid2';
import Radio from '@mui/material/Radio';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import RadioGroup from '@mui/material/RadioGroup';
import LoadingButton from '@mui/lab/LoadingButton';
import FormControlLabel from '@mui/material/FormControlLabel';

import ConfirmModal from 'src/components/ConfirmModal';
import { useGetRejectReason } from 'src/pages/ProjectDetailsPage/api';

import messages from './messages';

interface RejectReasonModalProps extends UCM.AcceptRejectModalType {
  description?: string;
  bulkDescription?: string;
}

export default function RejectReasonModal({
  type,
  modalLabel,
  buttonLabel,
  disabledButton,
  enabledFetch,
  isLoading,
  onConfirm,
  open,
  onClose,
  disabledBulkButton,
  bulkButtonLabel,
  description,
  bulkDescription,
}: RejectReasonModalProps) {
  const theme = useTheme();
  const { formatMessage } = useIntl();

  const [reason, setReason] = useState('');
  const [bulkAction, setBulkAction] = useState(false);
  const scopeDescription = bulkAction ? bulkDescription : description;

  const { data: rejectReasons } = useGetRejectReason(
    { reason_for: type },
    { enabled: enabledFetch },
  );

  const handleSubmit = useCallback(async () => {
    if (typeof onConfirm == 'function') {
      return onConfirm(reason, bulkAction);
    }
  }, [onConfirm, reason, bulkAction]);

  const handleOpenModal = useCallback(
    (
      handleOpen: (event: React.MouseEvent<HTMLElement>) => void,
      isBulk: boolean,
    ) =>
      (event: React.MouseEvent<HTMLElement>) => {
        setBulkAction(isBulk);
        handleOpen(event);
      },
    [],
  );

  const handleChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      setReason(event.target.value);
    },
    [],
  );

  if (!rejectReasons) {
    return null;
  }

  return (
    <ConfirmModal
      open={open}
      onClose={onClose}
      disabled={!reason}
      loading={isLoading}
      title={modalLabel ?? ''}
      submitTextButton={formatMessage(messages.confirmButtonLabel)}
      width={theme.spacing(69)}
      onConfirm={handleSubmit}
      triggerButton={(handleOpen) => {
        return (
          <>
            {buttonLabel && (
              <LoadingButton
                color="error"
                variant="contained"
                loading={isLoading}
                disabled={disabledButton}
                onClick={handleOpenModal(handleOpen, false)}
              >
                {buttonLabel}
              </LoadingButton>
            )}

            {bulkButtonLabel && (
              <Button
                color="error"
                variant="outlined"
                disabled={disabledBulkButton}
                onClick={handleOpenModal(handleOpen, true)}
              >
                {bulkButtonLabel}
              </Button>
            )}
          </>
        );
      }}
    >
      <Grid p={1.5}>
        {scopeDescription && (
          <Typography variant="body2" color="text.secondary" mb={1.5}>
            {scopeDescription}
          </Typography>
        )}
        <RadioGroup
          defaultValue=""
          name="radio-buttons-group"
          onChange={handleChange}
        >
          {rejectReasons.map((item) => {
            return (
              <FormControlLabel
                key={item.value}
                value={item.value}
                control={<Radio />}
                label={item.label}
              />
            );
          })}
        </RadioGroup>
      </Grid>
    </ConfirmModal>
  );
}
